import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, model } from '@angular/core';
import { Subject } from 'rxjs';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';

/** Distinguishes the explanation paragraphs when two create forms are mounted at once. */
let nextLockedReasonId = 0;

/**
 * Where a new item's tenant is CHOSEN, drawn in Figma `126-160652` (#1016 r2 rows 2a–2c).
 *
 * Renders only inside an accepted team — the caller decides that by whether it has a
 * {@link WorkspaceIdentity} at all. In a personal workspace there is no second destination to pick, so
 * there is no control.
 *
 * The two options are the caller's member tenant and the space, which are exactly the two tenants the
 * rail's My Workspace / Team Workspace sections resolve to; the default is whichever section is
 * selected, so the dialog opens agreeing with the rail rather than with a hardcoded side. No role chip:
 * the frame shows the space by title only, and a role would imply the picker grants access it does not.
 *
 * The frame labels the banner "task" — a copy defect in the frame, since the surface may be any item;
 * the noun comes from {@link itemNoun} so every form says its own without a second component.
 *
 * The padlock is `sapphire-lock`, added in #1016 r2 from the Iconsax glyph Pavel supplied — it was
 * missing from the icon set, not from the design. The banner carries the frame's `information-circle`
 * (`sapphire-info`).
 *
 * The frame has NO standalone "who can view" label: the control names itself, and the button carries
 * that sentence as its accessible name instead, so the row reads as one control rather than a
 * label-plus-control that screen readers announce twice.
 *
 * ADOPTION, for an app lane's own round. Declare WorkspaceIdentityService in your create form's own
 * providers[], hold its current() in a signal, and render this component above the form's first field,
 * gated on the same two conditions notes uses - the item has no id yet, and the identity resolved:
 *
 *     @if (!file?.id && workspaceIdentity(); as identity) {
 *       <app-tenancy-selector [spaceTitle]="identity.spaceTitle" [spaceTenantId]="identity.spaceTenantId"
 *         [memberTenantId]="identity.memberTenantId" itemNoun="file" [(tenantId)]="sharingTenantId" />
 *     }
 *
 * itemNoun is REQUIRED and is the noun your banner will say. locked is optional and false by default: bind
 * it to whatever makes the destination unchangeable in your app - playbook §2 rule 4, "on an unsaved item
 * the first attachment locks the choice", which all four shipped hosts bind to their own uploads control.
 * The Save-button label is YOURS, computed in the host, not in this component. In a private profile
 * current() emits undefined and nothing renders - that is Pavel's "no tenancy switcher in private
 * workspace" rule, and it lives in the caller's @if, not in here. Files mounts it in
 * file-form.component.html above <app-files-input> (:296) and in folder-form.component.html above the title
 * input; that edit belongs to another change.
 *
 * WHERE THE CHOSEN TENANT HAS TO ARRIVE - not "somewhere in the request" (#1128 review F3). The chosen
 * tenantId becomes the {@link OWNING_SPACE_ID} HttpContextToken (`spaces.interceptor.ts:33`) on the create
 * request's OWN context; the interceptor honours it ahead of the url prefix table and turns it into
 * `?space_id=`, which is why it also works for a create whose url has no trailing slash. Two seams put it
 * there and an app uses whichever its service takes:
 *   - `owningTenantContext(tenantId)` (`spaces.interceptor.ts:39`) - one call returning the HttpContext,
 *     or undefined for a null tenant; tasking passes it as `{ requestContext: ... }`
 *     (`task-form.component.ts:175-176`, `project-form.component.ts:323`), notes builds the same token
 *     by hand (`note-form.component.ts:250`).
 *   - `requestContextForTenantItems(items, config)` (`base-stitch.service.ts:91`) - what every
 *     BaseStitchService method already calls when the host hands it `{ tenantItems: [item] }`.
 * An EXISTING item is not this control's business: it is written back to its OWN accountId through
 * `{ tenantItems: [this.stitchItem] }` (`note-form.component.ts:241-243`, spec invariant 7), never to
 * this picker's value and never to the rail's selection. So a host's request config asks two questions in
 * this order - does the item already have an accountId, and only if not, what did this control choose.
 *
 * THE SEED, ONE RULE, because a form that skips it mounts lying. Seed your `sharingTenantId` from your
 * app's `effectiveTenantId()` in the constructor - the rail's selected section, so the dialog opens
 * agreeing with the rail (playbook §2 rule 3) - and RE-SEED when the identity arrives, with
 * `effectiveTenantId() ?? identity.memberTenantId`, guarded on the item having no id yet. The fallback is
 * the point: where the rail has not resolved, a bare `?? null` leaves the bound model null while this
 * control reads "Private to me", and the create then omits `space_id` altogether and lands in whatever
 * the surrounding context resolved to instead of the member tenant. All four shipped hosts carry that
 * shape; #1128 review F3 aligned notes to tasking's rather than leaving two dialects.
 */
@Component({
  selector: 'app-tenancy-selector',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Button2Component, IconComponent, PopoverDirective],
  templateUrl: './tenancy-selector.component.html',
})
export class TenancySelectorComponent {
  public readonly spaceTitle = input.required<string>();
  public readonly spaceTenantId = input.required<string>();
  public readonly memberTenantId = input.required<string>();

  /**
   * Required rather than defaulted: one shared default would make a files dialog silently say "note".
   * `strictTemplates` turns an omitted binding into a build error, which is where this is caught.
   */
  public readonly itemNoun = input.required<string>();
  public readonly locked = input(false);
  /**
   * Why the destination is locked, in the caller's words — the trigger's tooltip and the sentence under it. Null
   * says it is the attached files, the lock every host shipped with; a host that locks for another reason must say
   * which, or the screen states a false one (#1447 R3c).
   */
  public readonly lockedReason = input<string | null>(null);

  /** The chosen tenant. Two-way, so the form owns it and Save reads one value. */
  public readonly tenantId = model.required<string>();

  /** Associates the locked trigger with the sentence explaining why, per `CLAUDE.rules.md` §4e-quater. */
  protected readonly lockedReasonId = `tenancy-selector-locked-${nextLockedReasonId++}`;

  /**
   * Closes the audience menu, via the popover's own `showUntil` stream — the house pattern
   * (`contacts-panel`, `connections`), which `PopoverDirective` merges into its hide stream.
   *
   * Picking an audience has to dismiss the menu: the choice is applied immediately, so leaving it open
   * shows a menu whose state has already changed under the pointer and invites a second click that
   * does nothing. The plain `<button>` rows cannot use `CLOSE_POPOVER` — that token is provided by
   * `PopoverComponent` to a projected COMPONENT, and this menu is an `ng-template` rendered in the host
   * component's injector, so the token is not reachable here.
   */
  protected readonly closeMenu = new Subject<void>();

  protected readonly shared = computed(() => this.tenantId() === this.spaceTenantId());

  protected readonly controlLabel = computed(() =>
    this.shared() ? `Everyone at ${this.spaceTitle()}` : 'Private to me',
  );

  protected readonly bannerText = computed(() =>
    this.shared()
      ? 'This is a shared item in your team workspace'
      : `Only you can see this ${this.itemNoun()} in your workspace`,
  );

  public constructor() {
    inject(DestroyRef).onDestroy(() => this.closeMenu.complete());
  }

  protected selectPrivate(): void {
    if (this.locked()) {
      return;
    }
    this.tenantId.set(this.memberTenantId());
    this.closeMenu.next();
  }

  protected selectShared(): void {
    if (this.locked()) {
      return;
    }
    this.tenantId.set(this.spaceTenantId());
    this.closeMenu.next();
  }
}
